import React from "react";
import { useT } from "../i18n/LanguageContext.jsx";

// Public developer documentation page. No auth required — it only describes
// HOW to authenticate when calling the API from any HTTP client.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CURL_SNIPPET = `# 1. Get a Google ID token (one-time gcloud login required)
TOKEN=$(gcloud auth print-identity-token \\
  --audiences=778721425262-e1l5aj06hafeqrg4smfvqekah119eqvt.apps.googleusercontent.com)

# 2. Call the API
curl -X POST ${BACKEND_URL}/api/calculate \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "patient": {
      "neu_X": 346, "neu_Y": 492, "neu_Z": 1892,
      "lin_X": 92,  "lin_Y": 737, "lin_Z": 976,
      "mon_X": 210, "mon_Y": 1072,"mon_Z": 1313,
      "rdw_cv": 13.4, "rdw_sd": 46.3,
      "neu_": 9.74,   "lin_": 2.44,  "mon_": 0.73,
      "NLR__9_path_": 3.99, "MLR": 0.3, "PLR": 121,
      "plt": 295, "pct": 0.31, "pdw": 16.5
    }
  }'`;

const PYTHON_SNIPPET = `# pip install google-auth-oauthlib requests
import requests
from google_auth_oauthlib.flow import InstalledAppFlow

CLIENT_ID = "778721425262-e1l5aj06hafeqrg4smfvqekah119eqvt.apps.googleusercontent.com"
CLIENT_SECRET = "<paste your OAuth Desktop client secret here>"

flow = InstalledAppFlow.from_client_config(
    {"installed": {
        "client_id":     CLIENT_ID,
        "client_secret": CLIENT_SECRET,
        "auth_uri":      "https://accounts.google.com/o/oauth2/auth",
        "token_uri":     "https://oauth2.googleapis.com/token",
        "redirect_uris": ["http://localhost"],
    }},
    scopes=["openid", "email"],
)
creds = flow.run_local_server(port=0)   # opens browser once, returns
id_token = creds.id_token               # short-lived (1h), refreshable

resp = requests.post(
    "${BACKEND_URL}/api/calculate",
    headers={"Authorization": f"Bearer {id_token}"},
    json={"patient": {
        "neu_X": 346, "neu_Y": 492, "neu_Z": 1892,
        "lin_X": 92,  "lin_Y": 737, "lin_Z": 976,
        "mon_X": 210, "mon_Y": 1072,"mon_Z": 1313,
        "rdw_cv": 13.4, "rdw_sd": 46.3,
        "neu_": 9.74,   "lin_": 2.44,  "mon_": 0.73,
        "NLR__9_path_": 3.99, "MLR": 0.3, "PLR": 121,
        "plt": 295, "pct": 0.31, "pdw": 16.5,
    }},
    timeout=30,
)
resp.raise_for_status()
print(resp.json()["combined"])
# -> {"name": "LDA + Decision Trees (combined)", "probability_sepsis": 0.357..., ...}`;

const ENDPOINTS = [
  { method: "POST", path: "/api/calculate", auth: true, desc: "docs.ep.calculate" },
  { method: "GET",  path: "/api/health",   auth: false, desc: "docs.ep.health" },
  { method: "GET",  path: "/api/config",   auth: false, desc: "docs.ep.config" },
  { method: "GET",  path: "/api/examples", auth: false, desc: "docs.ep.examples" },
  { method: "PUT",  path: "/api/config",   auth: true,  desc: "docs.ep.configPut" },
  { method: "POST", path: "/api/config/reset",  auth: true, desc: "docs.ep.configReset" },
  { method: "POST", path: "/api/config/extract", auth: true, desc: "docs.ep.configExtract" },
  { method: "GET",  path: "/api/config/allowed-emails", auth: true, desc: "docs.ep.emailsGet" },
  { method: "PUT",  path: "/api/config/allowed-emails", auth: true, desc: "docs.ep.emailsPut" },
];

// Renders `code` and **bold** markers inside translated strings.
function Rich({ text }) {
  return text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith("`") && part.endsWith("`"))
      return <code key={i} className="font-mono text-[12px]">{part.slice(1, -1)}</code>;
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={i}>{part.slice(2, -2)}</strong>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

const PAYLOAD = `{
  "patient": {
    "neu_X": 346.0,     // Neutrophils — value X
    "neu_Y": 492.0,     // Neutrophils — value Y
    "neu_Z": 1892.0,    // Neutrophils — value Z
    "lin_X": 92.0,      // Lymphocytes — value X
    "lin_Y": 737.0,     // Lymphocytes — value Y
    "lin_Z": 976.0,     // Lymphocytes — value Z
    "mon_X": 210.0,     // Monocytes  — value X
    "mon_Y": 1072.0,    // Monocytes  — value Y
    "mon_Z": 1313.0,    // Monocytes  — value Z
    "rdw_cv": 13.4,     // RDW (CV)
    "rdw_sd": 46.3,     // RDW (SD)
    "neu_": 9.74,       // Neutrophils summary
    "lin_": 2.44,       // Lymphocytes summary
    "mon_": 0.73,       // Monocytes summary
    "NLR__9_path_": 3.99, // Neutrophil/Lymphocyte ratio
    "MLR": 0.3,         // Monocyte/Lymphocyte ratio
    "PLR": 121,         // Platelet/Lymphocyte ratio
    "plt": 295,         // Platelets
    "pct": 0.31,        // Plateletcrit
    "pdw": 16.5         // Platelet distribution width
  }
}`;

function CodeBlock({ children, testid }) {
  const { t } = useT();
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignored */
    }
  };
  return (
    <div className="relative border border-slate-200 bg-slate-50" data-testid={testid}>
      <button
        onClick={onCopy}
        data-testid={`${testid}-copy`}
        className="absolute top-2 right-2 px-2 py-1 text-[10px] font-mono border border-slate-300 bg-white hover:bg-slate-100"
      >
        {copied ? t("docs.copied") : t("docs.copy")}
      </button>
      <pre className="overflow-x-auto p-4 text-[12px] leading-relaxed font-mono text-slate-800 whitespace-pre">
        {children}
      </pre>
    </div>
  );
}

export default function ApiDocsPage() {
  const { t } = useT();
  // No translations for code (universal); UI labels do go through i18n.
  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="api-docs-page">
      <div className="mb-10">
        <div className="data-label mb-2">{t("docs.kicker")}</div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
          {t("docs.title")}
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          <Rich text={t("docs.intro")} />
        </p>
      </div>

      <section className="mb-12">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{t("docs.interactive.title")}</h2>
        <p className="text-sm text-slate-600 max-w-3xl mb-4">
          <Rich text={t("docs.interactive.body")} />
        </p>
        <div className="flex flex-wrap items-center gap-2" data-testid="openapi-links">
          <a
            href={`${BACKEND_URL}/api/docs`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-swagger-ui"
            className="px-4 py-2 text-sm font-medium bg-slate-900 text-white border border-slate-900 hover:bg-slate-800 inline-flex items-center gap-2"
          >
            {t("docs.interactive.swagger")}
            <span aria-hidden="true" className="font-mono text-xs">↗</span>
          </a>
          <a
            href={`${BACKEND_URL}/api/redoc`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-redoc"
            className="px-4 py-2 text-sm font-mono border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-2"
          >
            ReDoc
            <span aria-hidden="true" className="text-xs">↗</span>
          </a>
          <a
            href={`${BACKEND_URL}/api/openapi.json`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-openapi-json"
            className="px-4 py-2 text-sm font-mono border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-2"
          >
            openapi.json
            <span aria-hidden="true" className="text-xs">↗</span>
          </a>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{t("docs.baseUrl")}</h2>
        <div className="border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-sm text-slate-800" data-testid="base-url">
          {BACKEND_URL}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{t("docs.auth.title")}</h2>
        <p className="text-sm text-slate-600 max-w-3xl mb-4">
          <Rich text={t("docs.auth.intro")} />
        </p>
        <ol className="list-decimal pl-6 text-sm text-slate-700 space-y-2 max-w-3xl">
          <li data-testid="docs-auth-google">
            <strong>{t("docs.auth.google.title")}</strong>{" "}
            <Rich text={t("docs.auth.google.body").replace("{clientId}", `\`${process.env.REACT_APP_GOOGLE_CLIENT_ID}\``)} />
          </li>
          <li data-testid="docs-auth-app">
            <strong>{t("docs.auth.app.title")}</strong> <Rich text={t("docs.auth.app.body")} />
          </li>
        </ol>
        <p className="text-sm text-slate-600 mt-4 max-w-3xl">
          <Rich text={t("docs.auth.errors")} />
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{t("docs.endpoints.title")}</h2>
        <div className="border border-slate-200 overflow-x-auto" data-testid="endpoints-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <th className="px-4 py-3 data-label">{t("docs.col.method")}</th>
                <th className="px-4 py-3 data-label">{t("docs.col.path")}</th>
                <th className="px-4 py-3 data-label">{t("docs.col.auth")}</th>
                <th className="px-4 py-3 data-label">{t("docs.col.desc")}</th>
              </tr>
            </thead>
            <tbody>
              {ENDPOINTS.map((e) => (
                <tr key={e.method + e.path} className="row-hover border-b last:border-b-0 border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs text-slate-900 whitespace-nowrap">{e.method}</td>
                  <td className="px-4 py-2 font-mono text-[12px] text-slate-900 whitespace-nowrap">{e.path}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {e.auth ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-900 border border-slate-300 px-2 py-0.5">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        {t("docs.bearerRequired")}
                      </span>
                    ) : (
                      <span className="text-[11px] font-mono text-slate-400">{t("docs.public")}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{t(e.desc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{t("docs.payload.title")}</h2>
        <p className="text-sm text-slate-600 mb-3 max-w-3xl">
          <Rich text={t("docs.payload.body")} />
        </p>
        <CodeBlock testid="code-payload">{PAYLOAD}</CodeBlock>
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{t("docs.curl.title")}</h2>
        <p className="text-sm text-slate-600 mb-3 max-w-3xl">
          <Rich text={t("docs.curl.body")} />
        </p>
        <CodeBlock testid="code-curl">{CURL_SNIPPET}</CodeBlock>
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{t("docs.python.title")}</h2>
        <p className="text-sm text-slate-600 mb-3 max-w-3xl">
          <Rich text={t("docs.python.body")} />
        </p>
        <CodeBlock testid="code-python">{PYTHON_SNIPPET}</CodeBlock>
      </section>

      <section className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{t("docs.s2s.title")}</h2>
        <p className="text-sm text-slate-600 max-w-3xl">
          <Rich text={t("docs.s2s.body")} />
        </p>
      </section>
    </div>
  );
}
